import React, { useEffect, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import cx from 'classnames';

import { SortOptions, SortType } from '../../../enums/SORT';
import styles from './DateSortHeader.module.scss';

type DateSortOption = {
  label: string;
  value: SortOptions;
  order: SortType;
};

type DateSortHeaderProps = {
  sortOption: SortOptions | null;
  sort: SortType;
  onSelect: (_: SortOptions, option: DateSortOption) => void;
  compact?: boolean;
  disabled?: boolean;
  className?: string;
  arrowClassName?: string;
};

const DATE_SORT_OPTIONS: DateSortOption[] = [
  { label: 'Last Modified Asc', value: SortOptions.UPDATED_AT, order: SortType.ASC },
  { label: 'Last Modified Desc', value: SortOptions.UPDATED_AT, order: SortType.DESC },
  { label: 'Created At Asc', value: SortOptions.CREATED_AT, order: SortType.ASC },
  { label: 'Created At Desc', value: SortOptions.CREATED_AT, order: SortType.DESC },
];

export const getDateColumnSortOption = (sortOption?: SortOptions | null) =>
  sortOption === SortOptions.CREATED_AT ? SortOptions.CREATED_AT : SortOptions.UPDATED_AT;

export const getDateColumnLabel = (sortOption?: SortOptions | null, compact = false) => {
  if (getDateColumnSortOption(sortOption) === SortOptions.CREATED_AT) {
    return compact ? 'Created' : 'Created At';
  }

  return compact ? 'Modified' : 'Last Modified';
};

export const getDateColumnValue = <T extends { created_at?: string; updated_at?: string }>(
  item: T,
  sortOption?: SortOptions | null,
) =>
  getDateColumnSortOption(sortOption) === SortOptions.CREATED_AT
    ? item.created_at || item.updated_at || ''
    : item.updated_at || item.created_at || '';

export const getDateColumnTooltipLabel = (sortOption?: SortOptions | null) =>
  getDateColumnSortOption(sortOption) === SortOptions.CREATED_AT ? 'Last Modified' : 'Created At';

export const getDateColumnTooltipValue = <T extends { created_at?: string; updated_at?: string }>(
  item: T,
  sortOption?: SortOptions | null,
) =>
  getDateColumnSortOption(sortOption) === SortOptions.CREATED_AT
    ? item.updated_at || item.created_at || ''
    : item.created_at || item.updated_at || '';

const DateSortHeader: React.FC<DateSortHeaderProps> = ({
  sortOption,
  sort,
  onSelect,
  compact = false,
  disabled = false,
  className,
  arrowClassName,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [opened, setOpened] = useState(false);
  const activeDateSort = sortOption === SortOptions.UPDATED_AT || sortOption === SortOptions.CREATED_AT;

  useEffect(() => {
    if (!opened) return undefined;

    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpened(false);
      }
    };

    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [opened]);

  const selectOption = (option: DateSortOption) => {
    onSelect(option.value, option);
    setOpened(false);
  };

  return (
    <div ref={ref} className={cx(styles.wrapper, className, disabled && styles.disabled)}>
      <button
        type="button"
        className={styles.button}
        onClick={(event) => {
          event.stopPropagation();
          if (!disabled) setOpened((prev) => !prev);
        }}
      >
        {getDateColumnLabel(sortOption, compact)}
        {activeDateSort && sort === SortType.DESC && <KeyboardArrowDownIcon className={cx(styles.arrow, arrowClassName)} />}
        {activeDateSort && sort === SortType.ASC && <KeyboardArrowUpIcon className={cx(styles.arrow, arrowClassName)} />}
      </button>
      {opened && (
        <div className={styles.dropdown}>
          {DATE_SORT_OPTIONS.map((option) => (
            <div
              key={`${option.value}-${option.order}`}
              className={cx(
                styles.option,
                sortOption === option.value && sort === option.order && styles.selectedOption,
              )}
              onClick={(event) => {
                event.stopPropagation();
                selectOption(option);
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateSortHeader;
